/**
 * Service de cache performance pour les formulaires candidat/référent
 * Cache intelligent avec TTL pour éviter les rechargements inutiles
 * Optimise les performances sans complexité excessive
 */

interface CacheEntry<T> {
  data: T;
  timestamp: number;
  ttl: number; // Time To Live en millisecondes
}

class PerformanceCache {
  private cache = new Map<string, CacheEntry<any>>();
  
  // TTL par défaut : 5 minutes pour les données de formulaire
  private readonly DEFAULT_TTL = 5 * 60 * 1000;

  /**
   * Génère une clé de cache unique basée sur le type et l'identifiant
   */
  private generateKey(type: string, identifier: string): string {
    return `${type}_${identifier}`;
  }

  /**
   * Vérifie si une entrée de cache est encore valide
   */
  private isValid<T>(entry: CacheEntry<T>): boolean {
    return Date.now() - entry.timestamp < entry.ttl;
  }

  /**
   * Récupère une donnée du cache si elle est valide
   */
  get<T>(type: string, identifier: string): T | null {
    const key = this.generateKey(type, identifier);
    const entry = this.cache.get(key);
    
    if (!entry) {
      return null;
    }
    
    if (!this.isValid(entry)) {
      // Nettoyer les entrées expirées
      this.cache.delete(key);
      return null;
    }
    
    console.log(`🚀 Cache HIT pour ${key}`);
    return entry.data;
  }

  /**
   * Stocke une donnée dans le cache avec TTL optionnel
   */
  set<T>(type: string, identifier: string, data: T, ttl?: number): void {
    const key = this.generateKey(type, identifier);
    const entry: CacheEntry<T> = {
      data,
      timestamp: Date.now(),
      ttl: ttl || this.DEFAULT_TTL
    };
    
    this.cache.set(key, entry);
    console.log(`💾 Cache SET pour ${key} (TTL: ${entry.ttl}ms)`);
  }

  /**
   * Supprime une entrée spécifique du cache
   */
  invalidate(type: string, identifier: string): void {
    const key = this.generateKey(type, identifier);
    this.cache.delete(key);
    console.log(`🗑️ Cache INVALIDATED pour ${key}`);
  }

  /**
   * Nettoie toutes les entrées expirées
   */
  cleanup(): void {
    const now = Date.now();
    let cleanedCount = 0;
    
    for (const [key, entry] of this.cache.entries()) {
      if (now - entry.timestamp >= entry.ttl) {
        this.cache.delete(key);
        cleanedCount++;
      }
    }
    
    if (cleanedCount > 0) {
      console.log(`🧹 Cache cleanup: ${cleanedCount} entrées expirées supprimées`);
    }
  }

  /**
   * Vide complètement le cache
   */
  clear(): void {
    const size = this.cache.size;
    this.cache.clear();
    console.log(`🗑️ Cache vidé: ${size} entrées supprimées`);
  }

  /**
   * Retourne les statistiques du cache
   */
  getStats(): { size: number; keys: string[] } {
    return {
      size: this.cache.size,
      keys: Array.from(this.cache.keys())
    };
  }

  /**
   * Wrapper pour les fonctions async avec cache automatique
   */
  async withCache<T>(
    type: string,
    identifier: string,
    fetchFunction: () => Promise<T>,
    ttl?: number
  ): Promise<T> {
    // Vérifier le cache d'abord
    const cached = this.get<T>(type, identifier);
    if (cached !== null) {
      return cached;
    }

    // Charger les données et les mettre en cache
    console.log(`🔄 Cache MISS pour ${type}_${identifier}, chargement...`);
    const data = await fetchFunction();
    this.set(type, identifier, data, ttl);
    
    return data;
  }
}

// Instance singleton
export const performanceCache = new PerformanceCache();

// Types de cache pour les formulaires
export const CACHE_TYPES = {
  REFERENT_DATA: 'referent_data',
  CAMPAIGN_DATA: 'campaign_data',
  BRANDING_DATA: 'branding_data',
  CANDIDATE_STATUS: 'candidate_status',
  EVALUATION_CRITERIA: 'evaluation_criteria'
} as const;

// TTL spécifiques par type de données
export const CACHE_TTL = {
  REFERENT_DATA: 10 * 60 * 1000,    // 10 minutes - données stables
  CAMPAIGN_DATA: 10 * 60 * 1000,    // 10 minutes - données stables  
  BRANDING_DATA: 30 * 60 * 1000,    // 30 minutes - très stables
  CANDIDATE_STATUS: 2 * 60 * 1000,  // 2 minutes - peut changer
  EVALUATION_CRITERIA: 15 * 60 * 1000 // 15 minutes - stables
} as const;

// Nettoyage automatique toutes les 5 minutes
setInterval(() => {
  performanceCache.cleanup();
}, 5 * 60 * 1000);

// Helpers pour les cas d'usage courants
export const cacheHelpers = {
  /**
   * Cache pour les données référent
   */
  getReferentData: <T>(token: string) => 
    performanceCache.get<T>(CACHE_TYPES.REFERENT_DATA, token),
  
  setReferentData: <T>(token: string, data: T) => 
    performanceCache.set(CACHE_TYPES.REFERENT_DATA, token, data, CACHE_TTL.REFERENT_DATA),

  /**
   * Cache pour les données campagne
   */
  getCampaignData: <T>(token: string) => 
    performanceCache.get<T>(CACHE_TYPES.CAMPAIGN_DATA, token),
  
  setCampaignData: <T>(token: string, data: T) => 
    performanceCache.set(CACHE_TYPES.CAMPAIGN_DATA, token, data, CACHE_TTL.CAMPAIGN_DATA),

  /**
   * Cache pour le branding
   */
  getBrandingData: <T>(campaignId: string) => 
    performanceCache.get<T>(CACHE_TYPES.BRANDING_DATA, campaignId),
  
  setBrandingData: <T>(campaignId: string, data: T) => 
    performanceCache.set(CACHE_TYPES.BRANDING_DATA, campaignId, data, CACHE_TTL.BRANDING_DATA),

  /**
   * Invalider le cache après soumission
   */
  invalidateFormData: (token: string) => {
    performanceCache.invalidate(CACHE_TYPES.REFERENT_DATA, token);
    performanceCache.invalidate(CACHE_TYPES.CAMPAIGN_DATA, token);
    performanceCache.invalidate(CACHE_TYPES.CANDIDATE_STATUS, token);
  }
};